/**
 * Auth Permissions
 * Extracts permissions from roles, converts user data to StoredUser format,
 * and performs auth API calls
 */

import { API_CONFIG, getFullAdminUrl } from '../config/api.config';
import { storageService, StoredUser } from '../utils/storage';
import { fetchModulePermissions } from './module-permissions';

export const ALL_PERMISSIONS = [
  'dashboard',
  'clients',
  'branches',
  'providers',
  'employees',
  'hiring',
  'deactivated-employees',
  'purchases',
  'invoices',
  'invoice-hire',
  'bills',
  'contracts',
  'foa',
  'banks',
  'safes',
  'petty-cash',
  'deductions',
  'debit-notes',
  'payment-order',
  'requisitions',
  'delivery-notes',
  'categories',
  'currencies',
  'inventory',
  'cars',
  'crew-certificates',
  'document-settings',
  'crew-change',
  'crew-list',
  'crew-list-arabic',
  'active-crew-list-arabic',
  'ranks',
  'careers',
  'candidates',
  'personnel-coast-guard-permits',
  'car-coast-guard-permits',
  'personnel-letters',
  'car-letters',
  'raaft-letters',
  'taeen-letters',
  'points',
  'locations',
  'crew-evaluation',
  'vessel-report',
  'crew-employee-history',
  'roles',
  'system-users',
  'settings',
  'finance-reports',
  'code-prefixes',
  'employee-deductions',
  'timesheet',
  'salary-report',
  'transactions',
  'website-settings',
];

export function isSuperAdminRoles(roles: Array<{ code: string; name: string }>): boolean {
  return roles.some(
    (role) =>
      role.code === 'strapi-super-admin' ||
      role.name === 'Super Admin' ||
      role.name === 'Admin' ||
      role.code === 'admin'
  );
}

/** Super admins get every module key plus every action-level key
 *  (`<module>:create|read|update|delete|publish`). */
const ALL_PERMISSIONS_WITH_ACTIONS = [
  ...ALL_PERMISSIONS,
  ...ALL_PERMISSIONS.flatMap((m) =>
    ['create', 'read', 'update', 'delete', 'publish'].map((a) => `${m}:${a}`)
  ),
];

export function extractPermissionsFromRoles(roles: Array<{ code: string; name: string }>): string[] {
  if (isSuperAdminRoles(roles)) return ALL_PERMISSIONS_WITH_ACTIONS;
  // Fallback until the user's real permissions are resolved asynchronously
  return [];
}

/**
 * Resolve the modules a user can access. Super admins get everything;
 * everyone else gets modules derived from their role's real Strapi
 * permissions (GET /admin/users/me/permissions).
 */
export async function resolveModulePermissions(
  roles: Array<{ code: string; name: string }>,
  token: string
): Promise<string[]> {
  if (isSuperAdminRoles(roles)) return ALL_PERMISSIONS_WITH_ACTIONS;
  try {
    return await fetchModulePermissions(token);
  } catch (error) {
    console.error('❌ [AUTH] Failed to resolve module permissions:', error);
    return [];
  }
}

export function toStoredUser(data: any, roles?: Array<{ code: string; name: string }>): StoredUser {
  const userRoles = roles || data.roles || [];
  return {
    id: data.id || 0,
    username: (data.email || '').split('@')[0] || data.username || 'user',
    email: data.email || '',
    firstName: data.firstname || data.firstName || '',
    lastName: data.lastname || data.lastName || '',
    // Keep the raw role object too — pages check user.role.name for
    // role-specific behavior (crew evaluation approvals, evaluator role).
    role: userRoles?.[0] || null,
    roleName: userRoles?.[0]?.name || 'Admin',
    roleCode: userRoles?.[0]?.code || 'admin',
    permissions: extractPermissionsFromRoles(userRoles),
    isActive: data.isActive ?? true,
    blocked: data.blocked ?? false,
  };
}

/**
 * Strapi rotates the refresh cookie on every /admin/access-token call and the
 * old token becomes invalid immediately (single-use). When several requests
 * hit 401 at once, each one renewing independently would race: the first
 * rotation invalidates the token the others are about to present, so the
 * losers get a 401 and the user is kicked to login mid-session. Sharing one
 * in-flight renewal makes every caller wait for the same new token.
 */
let renewalPromise: Promise<string> | null = null;

/** Renew authentication token */
export function performRenewToken(): Promise<string> {
  if (!renewalPromise) {
    renewalPromise = doRenewToken().finally(() => {
      renewalPromise = null;
    });
  }
  return renewalPromise;
}

async function doRenewToken(): Promise<string> {
  try {
    const url = getFullAdminUrl(API_CONFIG.ENDPOINTS.ADMIN_AUTH.RENEW_TOKEN);

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to renew token');
    }

    const data = await response.json();

    // In cookie-auth mode the backend delivers the token as an httpOnly
    // cookie and omits it from the body; the sentinel tells the backend to
    // authenticate from the cookie.
    const token = data.data?.token || 'cookie-session';
    storageService.setToken(token);

    return token;
  } catch (error: any) {
    console.error('Renew token error:', error);
    throw error;
  }
}

/** Login admin user - performs fetch and returns { user, token } */
export async function performLogin(credentials: {
  email: string;
  password: string;
  rememberMe?: boolean;
}): Promise<{ user: StoredUser; token: string }> {
  const url = getFullAdminUrl(API_CONFIG.ENDPOINTS.ADMIN_AUTH.LOGIN);

  console.log('🔐 [AUTH] Starting login process...');
  console.log('📍 [AUTH] URL:', url);
  console.log('📧 [AUTH] Email:', credentials.email);

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  console.log('📡 [AUTH] Response status:', response.status, response.statusText);

  if (!response.ok) {
    const error = await response.json();
    console.error('❌ [AUTH] Login failed:', error);
    throw new Error(error.error?.message || 'Login failed');
  }

  const authData = await response.json();
  console.log('✅ [AUTH] Raw response from Strapi:', authData);

  const responseData = authData.data || authData;
  const userData = responseData.user;
  // In cookie-auth mode the token arrives as an httpOnly cookie only.
  const token = responseData.token || 'cookie-session';

  if (!userData) {
    console.error('❌ [AUTH] Missing user data in response');
    throw new Error('Invalid response from server');
  }

  let userRoles = userData.roles || [];

  if (!userRoles || userRoles.length === 0) {
    console.log('⚠️ [AUTH] No roles in login response, fetching user details...');
    try {
      const meUrl = getFullAdminUrl(API_CONFIG.ENDPOINTS.ADMIN_AUTH.ME);
      const meResponse = await fetch(meUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (meResponse.ok) {
        const meData = await meResponse.json();
        console.log('📋 [AUTH] /me endpoint response:', meData);
        userRoles = meData.data?.roles || [];
        console.log('✅ [AUTH] Fetched roles from /me endpoint:', userRoles);
      }
    } catch (error) {
      console.error('❌ [AUTH] Failed to fetch user roles:', error);
    }
  }

  const user = toStoredUser(userData, userRoles);
  user.permissions = await resolveModulePermissions(userRoles, token);

  console.log('💾 [AUTH] Storing user data:', user);
  console.log('✅ [AUTH] Login successful!');
  console.log('👥 [AUTH] Logged in as:', user.email);
  console.log('🎭 [AUTH] Role:', user.roleName);
  console.log('🔐 [AUTH] Permissions:', user.permissions);

  return { user, token };
}

/** Get current admin user info */
export async function performGetCurrentUser(): Promise<StoredUser> {
  const token = storageService.getToken();
  if (!token) {
    throw new Error('No authentication token found');
  }

  const url = getFullAdminUrl(API_CONFIG.ENDPOINTS.ADMIN_AUTH.ME);

  console.log('🔍 [AUTH] Fetching current user from:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  console.log('📡 [AUTH] /me response status:', response.status);

  if (!response.ok) {
    if (response.status === 401) {
      console.warn('⚠️ [AUTH] 401 on /me - attempting token renewal...');
      try {
        const newToken = await performRenewToken();
        if (newToken) {
          const retryResponse = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${newToken}`,
            },
          });
          if (retryResponse.ok) {
            const retryData = await retryResponse.json();
            const data = retryData.data || retryData;
            const user = toStoredUser(data);
            user.permissions = await resolveModulePermissions(data.roles || [], newToken);
            storageService.setUser(user);
            storageService.setPermissions(user.permissions);
            return user;
          }
        }
      } catch (renewError) {
        console.error('❌ [AUTH] Token renewal failed on /me 401:', renewError);
      }
      const authError: any = new Error('Unauthorized - token expired');
      authError.status = 401;
      authError.error = { status: 401, name: 'UnauthorizedError', message: 'Token expired' };
      throw authError;
    }
    const error = await response.json().catch(() => ({ error: { message: 'Failed to get user info' } }));
    throw new Error(error.error?.message || 'Failed to get user info');
  }

  const userData = await response.json();
  console.log('📋 [AUTH] /me response data:', userData);

  const data = userData.data || userData;
  const user = toStoredUser(data);
  user.permissions = await resolveModulePermissions(data.roles || [], token);

  console.log('✅ [AUTH] Current user loaded:', user.email);

  storageService.setUser(user);
  storageService.setPermissions(user.permissions);

  return user;
}

